
import { useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'

import { FormSearchInputComponent } from '@/modules/shared/components/form-search-input.component'
import { Button } from '@/modules/shared/components/ui/button'
import { Card, CardContent } from '@/modules/shared/components/ui/card'
import { Checkbox } from '@/modules/shared/components/ui/checkbox'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/modules/shared/components/ui/dialog'
import { ScrollArea } from '@/modules/shared/components/ui/scroll-area'
import { cn } from '@/modules/shared/lib/utils'

import { useModalOrderReplicateComponent } from '../../hooks/use-modal-order-replicate-component.hook'
import { formatCnpjCpf } from '@/modules/customer/helpers/format-fields-customer-form.helper'

type TModalOrderReplicateComponent = {
	isOpen: boolean
	onClose: () => void
}

export function ModalOrderReplicateComponent({
	isOpen,
	onClose,
}: TModalOrderReplicateComponent) {
	const {
		clientSelectedToReplicate,
		clients,
		isLoadingRequestClientToReplicate,
		isLoadingMore,
		hasMore,
		totalRecords,
		filterSearchEntity,
		handleSearchClient,
		handleSelectClientToReplicate,
		handleSubmitOrderReplicate,
		handleToggleOrderDiscount,
		replicateOrderDiscount,
		isLoadingReplicateOrder,
		handleCloseModal,
		loadMore,
	} = useModalOrderReplicateComponent({
		onClose,
	})

	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const loadingRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!hasMore || isLoadingMore) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					loadMore()
				}
			},
			{ threshold: 0.1 }
		)

		if (loadingRef.current) {
			observer.observe(loadingRef.current)
		}

		return () => observer.disconnect()
	}, [hasMore, isLoadingMore, loadMore])

	return (
		<Dialog open={isOpen} onOpenChange={handleCloseModal}>
			<DialogContent className="bg-white max-w-2xl max-h-screen">
				<DialogHeader>
					<DialogTitle>Replicar pedido</DialogTitle>
					{totalRecords > 0 && (
						<p className="text-sm text-muted-foreground">
							{totalRecords} {totalRecords === 1 ? 'cliente encontrado' : 'clientes encontrados'}
						</p>
					)}
				</DialogHeader>

				<div>
					<FormSearchInputComponent
						formSearchInputController={{
							iconOnly: true,
							filter: filterSearchEntity,
							filterDefault: 'razao_social_nome',
							placeholder: 'Pesquisar clientes',
							onSubmitRequest: handleSearchClient,
						}}
					/>
				</div>

				<ScrollArea ref={scrollAreaRef} className="h-[60vh]">
					<div className="space-y-2">
						{isLoadingRequestClientToReplicate ? (
							<div className="flex flex-col items-center justify-center h-64">
								<Loader2 size={32} className="animate-spin text-primary" />
								<p className="pt-6">Carregando clientes...</p>
							</div>
						) : clients.length > 0 ? (
							<>
								{clients.map((client) => {
									const isClientClickable = !!client.idTabelaPrecoVigencia && !client.flagSituacao
									
									return (
										<Card
											key={`${client.idEntidade}-${client.razaoSocialNome}`}
											className={cn(
												"mb-2 w-full transition-colors",
												isClientClickable
													? 'cursor-pointer hover:bg-muted/20'
													: 'bg-muted/10 cursor-default opacity-70'
											)}
											onClick={() => {
												if (isClientClickable) {
													handleSelectClientToReplicate(client.idEntidade)
												}
											}}
										>
											<CardContent className="p-4">
												<div className="flex items-start space-x-3">
													<Checkbox
														checked={clientSelectedToReplicate.includes(client.idEntidade)}
														disabled={!isClientClickable}
														onChange={() => {
															if (isClientClickable) {
																handleSelectClientToReplicate(client.idEntidade)
															}
														}}
														onClick={(e) => {
															e.stopPropagation()
															if (isClientClickable) {
																handleSelectClientToReplicate(client.idEntidade)
															}
														}}
													/>
													<div className="flex-1 min-w-0">
														<p className="font-semibold text-sm text-balance">
															{client.idEntidade} - {client.razaoSocialNome}
														</p>
														<p className="text-xs text-muted-foreground">{formatCnpjCpf(client.cnpjCpf)}</p>
														<p className="text-xs text-muted-foreground">
															{client.logradouro}, {client.numero}
														</p>
														<p className="text-xs text-muted-foreground">
															{client.cidade}, {client.uf}
														</p>
														
														{(!client.idTabelaPrecoVigencia || client.flagSituacao) && (
															<div className="mt-2">
																<span className="text-xs text-white rounded-md font-medium bg-red-600 px-2 py-1 inline-block">
																	{!client.idTabelaPrecoVigencia
																		? 'Cliente sem tabela de preço vigente'
																		: 'Cliente bloqueado para realizar vendas'}
																</span>
															</div>
														)}
													</div>
												</div>
											</CardContent>
										</Card>
									)
								})}

								{/* Loading indicator para scroll infinito */}
								{hasMore && (
									<div
										ref={loadingRef}
										className="flex items-center justify-center py-4"
									>
										{isLoadingMore && (
											<>
												<Loader2 size={20} className="animate-spin text-primary mr-2" />
												<span className="text-sm text-muted-foreground">
													Carregando mais clientes...
												</span>
											</>
										)}
									</div>
								)}

								{/* Indicador de fim da lista */}
								{!hasMore && clients.length > 10 && (
									<div className="flex items-center justify-center py-4">
										<span className="text-sm text-muted-foreground">
											Todos os clientes foram carregados
										</span>
									</div>
								)}
							</>
						) : (
							<div className="flex flex-col items-center justify-center h-64">
								<p className="text-gray-500 text-center">
									Lista vazia
								</p>
								<p className="text-gray-500 text-center">
									Nenhum cliente encontrado
								</p>
							</div>
						)}
					</div>
				</ScrollArea>

				<DialogFooter>
					<div className="flex flex-1">
						<div className="space-x-2 items-center flex flex-1">
							<Checkbox
								id="replicateOrderDiscount"
								checked={replicateOrderDiscount}
								onClick={handleToggleOrderDiscount}
							/>
							<label htmlFor="replicateOrderDiscount" className="text-sm">
								Manter desconto
							</label>
						</div>
						<Button
							onClick={handleSubmitOrderReplicate}
							disabled={isLoadingReplicateOrder || clientSelectedToReplicate.length === 0}
						>
							{isLoadingReplicateOrder && (
								<Loader2 className="animate-spin text-white mr-2" size={20} />
							)}
							Replicar ({clientSelectedToReplicate.length})
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}